import type { SolarComputeHostingPlan } from "~/api/dto/solar-proposal-compute.dto"

/**
 * Compute node copy for the Battery-Only presentation's section, under
 * Vendor Hosting Agreement Rev 2.10. The node and its terms are the ones
 * Solar offers; what differs is the framing: the payment sits beside the
 * battery's monthly payment, and neither the node's payments nor its
 * electricity are part of the battery savings estimate.
 *
 * Every dollar figure in the prose arrives already formatted from the
 * gateway's payout, so the copy never derives money and the stat, the ledger
 * and the FAQ name the same figures.
 */

/** The payout's figures, formatted for prose. */
export type BatteryComputePayoutCopy = {
  /** The guaranteed monthly base, e.g. "$400". */
  base: string
  /** The monthly bonus cap, e.g. "$400". */
  bonusCap: string
  /** The service term in years. */
  termYears: number
}

const BATTERY_COMPUTE_PLAN_NAMES: Record<SolarComputeHostingPlan, string> = {
  core: `Core`,
  plus: `Plus`,
  max: `Max`,
}

/**
 * The plan's customer name. A plan this client doesn't know yet (a newer
 * gateway's tier) reads as its own value, capitalized, never "undefined".
 */
export const batteryComputePlanName = (plan: string): string =>
  (BATTERY_COMPUTE_PLAN_NAMES as Partial<Record<string, string>>)[plan] ??
  plan.charAt(0).toUpperCase() + plan.slice(1)

export const batteryComputeLede = ({
  base,
  bonusCap,
  termYears,
}: BatteryComputePayoutCopy): string =>
  `Host a Vendor compute node beside your battery. Vendor pays you a fixed ${base} every month for ${termYears} years, plus a network bonus of up to ${bonusCap} when the network earns it.`

export const BATTERY_COMPUTE_LEDE_UNAVAILABLE = `Host a Vendor compute node beside your battery. Vendor pays you a fixed monthly base for the full service term, plus a network bonus when the network earns it.`

export const batteryComputeHowItWorks = (
  payout: BatteryComputePayoutCopy | null,
): { title: string; body: string }[] => [
  {
    title: `We install it indoors`,
    body: `A wall-mounted node in your garage or utility room. It plugs into a standard outlet, like a computer, and connects to your home internet.`,
  },
  {
    title: `You keep it online`,
    body: `Keep it powered and on a 1 Gbps internet connection. Vendor monitors it and handles every repair and upgrade.`,
  },
  {
    title: `You get paid monthly`,
    body: payout
      ? `A fixed ${payout.base} base plus a network bonus of up to ${payout.bonusCap}, paid by the 15th of the following month.`
      : `A fixed monthly base plus a network bonus, paid by the 15th of the following month.`,
  },
]

export const batteryComputeTermNote = (termYears: number): string =>
  `${termYears}-year term from activation · Vendor maintains and upgrades the node at no cost`

/** The story's four questions; "How you earn" names the plan's figures. */
export const batteryComputeStory = (
  payout: BatteryComputePayoutCopy | null,
  planName: string | null,
): {
  heading: string
  intro: string
  flow: { who: string; what: string; arrow: string | null }[]
  answers: { question: string; headline: string; body: string }[]
} => ({
  heading: `A supercomputer in your home, and you get rewarded for hosting it.`,
  intro: `Demand for computing power is growing faster than it can be built. Your Vendor compute node is a supercomputer that sells its computing time to customers like AI companies, research labs and more. Vendor owns it, finds the customers and pays you every month for hosting it.`,
  flow: [
    {
      who: `Compute customers`,
      what: `AI companies, research labs and more pay Vendor for computing time`,
      arrow: `buy compute`,
    },
    {
      who: `Vendor`,
      what: `Sells the capacity and sends the work across its network of compute nodes`,
      arrow: `sends work`,
    },
    {
      who: `Your compute node`,
      what: `Runs the work on your home's power and internet`,
      arrow: `rewards you monthly`,
    },
    {
      who: `You`,
      what: payout
        ? `${payout.base} fixed base every month, plus a share of network earnings up to ${payout.bonusCap}`
        : `A fixed base every month, plus a share of network earnings`,
      arrow: null,
    },
  ],
  answers: [
    {
      question: `Why`,
      headline: `The world needs more computing power.`,
      body: `Demand for compute is outpacing supply. Hosting a node puts more of it to work and gets you paid for providing the home it runs in.`,
    },
    {
      question: `What it does`,
      headline: `Sells computing time to Vendor's customers.`,
      body: `AI companies, research labs and other customers buy time on the network. You don't manage anything; Vendor finds the work and keeps the node running.`,
    },
    {
      question: `Who pays for it`,
      headline: `Vendor owns it and pays for its upkeep.`,
      body: `No purchase, lease or loan. Vendor covers maintenance, repairs, upgrades and replacements for the full term. You provide the space, the electricity and a 1 Gbps internet connection.`,
    },
    {
      question: `How you earn`,
      headline: `A fixed base, plus a reward when the network does well.`,
      body:
        payout && planName
          ? `The ${payout.base} base is paid every month whether or not your node is busy. When the network earns a surplus, half of it is shared across hosts as a bonus, up to ${payout.bonusCap} a month on the ${planName} plan.`
          : `The base is paid every month whether or not your node is busy. When the network earns a surplus, half of it is shared across hosts as a bonus, up to your plan's limit.`,
    },
  ],
})

/** "How the network bonus works", in plain words (B3). */
export const BATTERY_COMPUTE_BONUS = {
  lead: `Think of the bonus as profit sharing. Your node is one of many in Vendor's network. When the network brings in more money than it costs to run, Vendor shares half of the extra with its hosts. Bigger plans get a bigger share, up to their plan's limit.`,
  strong: {
    label: `A strong month`,
    headline: `The network earns more than it costs.`,
    body: `Half of the extra goes to hosts, so you get your base plus a bonus.`,
  },
  slow: {
    label: `A slow month`,
    headline: `The network only covers its costs.`,
    body: `There's nothing extra to share, so there's no bonus that month. Your base is still paid.`,
  },
  notes: [
    {
      title: `It's about the whole network.`,
      body: `How busy your own node is doesn't change your bonus.`,
    },
    {
      title: `Slow months don't carry over.`,
      body: `A slow month never reduces a later one.`,
    },
    {
      title: `You can see the math.`,
      body: `Your monthly statement shows what the network earned, its costs and your share.`,
    },
  ],
} as const

export const BATTERY_COMPUTE_SHARE_NOTE = `An illustration. You can authorize Vendor to pay part or all of your payment toward your electricity, internet, solar, financing or battery bills; what it pays to a bill comes out of the same payment, not on top of it. Only the base is guaranteed; the bonus may be $0.`

export const BATTERY_COMPUTE_SEPARATE_NOTE = `Shown apart from your battery savings: the node's payments and its electricity are not part of the battery savings estimate.`

export const batteryComputeFaq = (
  payout: BatteryComputePayoutCopy | null,
): { question: string; answer: string }[] => {
  const base = payout ? `${payout.base} monthly base` : `monthly base`
  const term = payout ? `${payout.termYears}-year term` : `service term`
  const fullTerm = payout ? `full ${payout.termYears} years` : `full term`
  const runs = payout
    ? `runs ${payout.termYears} years`
    : `runs for its full term`
  return [
    {
      question: `Is the income guaranteed?`,
      answer: `The ${base} is guaranteed for the ${term} from activation, whether or not your node is busy. The network bonus${payout ? `, up to ${payout.bonusCap} a month,` : ``} depends on what the whole Vendor network collects and may be $0. Payouts may be taxable.`,
    },
    {
      question: `Does the bonus kick in at a certain utilization?`,
      answer: `No. There's no threshold and no per-hour rate. The bonus grows with what the whole network earns above its costs, so busier months tend to pay more, but even a busy month can pay $0 if costs are high.`,
    },
    {
      question: `How and when am I paid?`,
      answer: `By the 15th of each month for the prior month. Take it as cash, or authorize Vendor to pay part or all of it toward your battery payment or your electricity, internet, solar or financing bills and pay you the rest.`,
    },
    {
      question: `Who owns and maintains the node?`,
      answer: `Vendor owns it and handles all maintenance, repairs, upgrades and replacements for the ${fullTerm} at no cost to you. Upgrades don't change your base or bonus cap.`,
    },
    {
      question: `Can I stop hosting?`,
      answer: `The agreement ${runs} and doesn't include a right to cancel for convenience. You keep any cancellation rights the law gives you, and you and Vendor can agree in writing to end it.`,
    },
    {
      question: `What if Vendor stops operating?`,
      answer: `If Vendor permanently stops providing the service before the term ends, it pays the node's appraised fair market value toward the financing on your battery.`,
    },
    {
      question: `Who pays for its electricity?`,
      answer: `It runs on your home's power and needs a 1 Gbps internet connection. That electricity is not part of your battery savings estimate, and the fixed base is Vendor's contribution toward those costs.`,
    },
  ]
}

export const batteryComputeDisclosure = (
  hasNodeLoad: boolean,
  payout:
    | (BatteryComputePayoutCopy & {
        planName: string
      })
    | null,
): string =>
  [
    `Illustrative figures based on the Vendor Hosting Agreement, Revision 2.10.`,
    payout
      ? `The ${payout.planName} Hosting Plan pays a fixed ${payout.base} monthly base from activation for the ${payout.termYears}-year service term; the variable network performance bonus is capped at ${payout.bonusCap} a month, is calculated from network-wide collected receipts under Section 2.2, and may be $0. Totals that include the bonus show its cap, not an estimate.`
      : null,
    BATTERY_COMPUTE_SEPARATE_NOTE,
    hasNodeLoad ? `Node electricity is modeled at 90% utilization.` : null,
    `Payments may be taxable; consult a tax advisor.`,
  ]
    .filter(Boolean)
    .join(` `)
